/**
 * BattlefieldHelp Component
 *
 * Container component that integrates all HelpOverlay pieces for the battlefield page.
 * Manages:
 * - Help overlay state (open/closed)
 * - First visit detection and auto-show
 * - Keyboard shortcuts for help toggle
 * - Device type detection (desktop/mobile)
 * - Pulse animation on first dismiss
 *
 * @module components/HelpOverlay/BattlefieldHelp
 */

import React, { useState, useCallback, useEffect } from 'react';
import { HelpOverlay } from './HelpOverlay';
import { HelpButton } from './HelpButton';
import { MobileHelpSheet } from './MobileHelpSheet';
import { useFirstVisit } from './useFirstVisit';
import { useHelpKeyboard } from './useHelpKeyboard';
import styles from './BattlefieldHelp.module.css';

interface BattlefieldHelpProps {
  /** Whether help functionality is enabled (can disable during combat transitions) */
  enabled?: boolean;
}

/**
 * BattlefieldHelp provides a complete help overlay system for the battlefield.
 *
 * Features:
 * - Auto-shows help on first visit after a delay
 * - Keyboard shortcuts: ? or H to toggle, Escape to close
 * - Responsive: shows desktop overlay or mobile bottom sheet
 * - Pulse animation on help button after first dismiss
 *
 * @param props - Component props
 * @param props.enabled - Whether help functionality is enabled (default: true)
 *
 * @example
 * ```tsx
 * // Basic usage - add to battlefield page
 * <BattlefieldHelp />
 *
 * // Disable during combat transitions
 * <BattlefieldHelp enabled={!isTransitioning} />
 * ```
 */
export function BattlefieldHelp({ enabled = true }: BattlefieldHelpProps) {
  const [helpOpen, setHelpOpen] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // First visit logic
  const { shouldShowHelp, markAsSeen, isFirstVisit } = useFirstVisit({});

  // Auto-open on first visit
  useEffect(() => {
    if (shouldShowHelp && enabled) {
      setHelpOpen(true);
    }
  }, [shouldShowHelp, enabled]);

  // Open handler
  const handleOpen = useCallback(() => {
    setHelpOpen(true);
    setShowPulse(false); // Clear pulse when opening
  }, []);

  // Close handler
  const handleClose = useCallback(() => {
    setHelpOpen(false);

    // Mark as seen and show pulse on first dismiss
    if (isFirstVisit) {
      markAsSeen();
      setShowPulse(true);
      // Clear pulse after animation
      setTimeout(() => setShowPulse(false), 1000);
    }
  }, [isFirstVisit, markAsSeen]);

  // Keyboard shortcuts
  useHelpKeyboard({
    enabled: enabled,
    isHelpOpen: helpOpen,
    onOpen: handleOpen,
    onClose: handleClose,
  });

  const deviceType = isMobile ? 'mobile' : 'desktop';

  return (
    <>
      {/* Help Button - positioned via CSS */}
      <div className={styles.helpButtonContainer}>
        <HelpButton onClick={handleOpen} showPulse={showPulse} />
      </div>

      {/* Help Overlay - desktop or mobile variant */}
      {isMobile ? (
        <MobileHelpSheet isOpen={helpOpen} onClose={handleClose} />
      ) : (
        <HelpOverlay isOpen={helpOpen} onClose={handleClose} deviceType={deviceType} />
      )}
    </>
  );
}

export default BattlefieldHelp;
